import {
  Content,
  Portal as RadixPortal,
  Trigger as RadixTrigger,
  Root,
} from "@radix-ui/react-popover";
import TimeIcon from "app/components/Icons/TimeIcon";
import Input from "app/components/Input";
import {
  formatEffortShorthand,
  parseEffortInput,
} from "app/containers/Features/helper";
import { EffortEstimatedType } from "app/containers/Features/types";
import React, { Dispatch, useEffect, useMemo, useState } from "react";

interface Props {
  effortEstimated: EffortEstimatedType[];
  setEdit: Dispatch<React.SetStateAction<boolean>>;
  ticketAssignee: string;
  onUpdate: (effortEstimated: EffortEstimatedType) => void;
}

const EffortEstimatedInput: React.FC<Props> = ({
  effortEstimated,
  setEdit,
  ticketAssignee,
  onUpdate,
}) => {
  const currentEffortEstimated = useMemo(
    () => effortEstimated.find((effort) => effort.memberId === ticketAssignee),
    [effortEstimated, ticketAssignee],
  );
  const formattedEffort = currentEffortEstimated
    ? formatEffortShorthand(
        currentEffortEstimated?.effort?.hours,
        currentEffortEstimated?.effort?.minutes,
      )
    : "";

  const [newValue, setNewValue] = useState(formattedEffort);
  const [menuVisibility, setMenuVisibility] = useState<boolean>(false);

  const parsedEffort = parseEffortInput(newValue);
  const handleSubmit = () => {
    if (parsedEffort.isValid) {
      const payload = {
        hours: parsedEffort.hours,
        minutes: parsedEffort.minutes,
        date: new Date().toISOString(),
      };
      onUpdate({
        _id: "",
        isLock: false,
        memberId: ticketAssignee,
        effort: {
          _id: "",
          ...payload,
        },
      });
      setMenuVisibility(false);
      setEdit(false);
    }
  };

  // Other Variables
  const shouldOpenPopover = parsedEffort.isValid;
  const isPopoverOpen = menuVisibility && shouldOpenPopover;

  // Effects
  useEffect(() => {
    setNewValue(formattedEffort);
  }, [formattedEffort]);

  return (
    <Root
      open={isPopoverOpen}
      onOpenChange={(visible) => setMenuVisibility(visible)}
    >
      <RadixTrigger asChild>
        <div>
          <Input
            id={"value"}
            autoFocus
            icon={<TimeIcon size={18} />}
            placeholder={"Enter efforts estimation"}
            value={newValue}
            onChange={(e) => {
              const nextValue = e.target.value;
              setNewValue(nextValue);
              setMenuVisibility(nextValue.trim().length > 0);
            }}
            onBlur={() => {
              setEdit(false);
              handleSubmit();
            }}
            inputClassName="!p-[6px] !h-[36px]"
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                handleSubmit();
              }
            }}
          />
        </div>
      </RadixTrigger>
      <RadixPortal>
        <Content
          align="start"
          sideOffset={2}
          onOpenAutoFocus={(event) => event.preventDefault()}
          className="min-w-[var(--radix-popover-trigger-width)] max-w-full flex flex-col bg-white rounded border border-gray-100 shadow-md z-50 py-2.5"
        >
          <p className="text-sm text-secondary px-2.5">
            {parsedEffort.formatted}
          </p>
        </Content>
      </RadixPortal>
    </Root>
  );
};

export default EffortEstimatedInput;
